import React, { useState } from "react";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCalendar,
  FiClock,
  FiCheckCircle,
  FiAlertCircle,
} from "react-icons/fi";

/**
 * Component để quản lý fixed date schedules
 */
const FixedDateScheduleUI = ({
  schedules,
  onAdd,
  onEdit,
  onDelete,
  onToggleCompleted,
}) => {
  // Show add form by default if no schedules exist
  const [isAddingNew, setIsAddingNew] = useState(schedules.length === 0);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newSchedule, setNewSchedule] = useState({
    date: "",
    times: ["08:00"], // Default to 8:00 AM (already a 5-minute interval)
  });
  const [filter, setFilter] = useState("all"); // all, pending, completed

  // Helper functions
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("vi-VN", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const isDateInPast = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getScheduleStatus = (schedule) => {
    if (schedule.completed) return "completed";
    if (isDateInPast(schedule.date)) return "overdue";
    return "pending";
  };

  const handleAddTime = () => {
    // Default to next 5-minute interval
    const now = new Date();
    const currentMinute = now.getMinutes();
    const nextMinute = Math.ceil(currentMinute / 5) * 5;
    const hour = nextMinute >= 60 ? (now.getHours() + 1) % 24 : now.getHours();
    const minute = nextMinute >= 60 ? 0 : nextMinute;
    const defaultTime = `${String(hour).padStart(2, "0")}:${String(
      minute
    ).padStart(2, "0")}`;

    setNewSchedule({
      ...newSchedule,
      times: [...newSchedule.times, defaultTime],
    });
  };

  const handleRemoveTime = (index) => {
    if (newSchedule.times.length > 1) {
      setNewSchedule({
        ...newSchedule,
        times: newSchedule.times.filter((_, i) => i !== index),
      });
    }
  };

  const handleTimeChange = (index, value) => {
    const updatedTimes = [...newSchedule.times];
    updatedTimes[index] = value;
    setNewSchedule({
      ...newSchedule,
      times: updatedTimes,
    });
  };

  const handleSaveNew = () => {
    if (!newSchedule.date) {
      alert("Vui lòng chọn ngày!");
      return;
    }

    // Validate date is in future
    if (isDateInPast(newSchedule.date)) {
      alert("Vui lòng chọn ngày trong tương lai!");
      return;
    }

    // Check for duplicate times
    const uniqueTimes = [...new Set(newSchedule.times)];
    if (uniqueTimes.length !== newSchedule.times.length) {
      alert("Có giờ đăng bị trùng lặp!");
      return;
    }

    onAdd({
      date: newSchedule.date,
      times: newSchedule.times,
      completed: false,
    });

    // Reset form
    setNewSchedule({ date: "", times: ["08:00"] });
    setIsAddingNew(false);
  };

  const handleCancelNew = () => {
    // Only allow cancel if there are existing schedules
    if (schedules.length > 0) {
      setNewSchedule({ date: "", times: ["08:00"] });
      setIsAddingNew(false);
    }
  };

  const handleEditSchedule = (index) => {
    setEditingIndex(index);
    setNewSchedule({
      date: schedules[index].date,
      times: [...schedules[index].times],
    });
  };

  const handleSaveEdit = () => {
    if (!newSchedule.date) {
      alert("Vui lòng chọn ngày!");
      return;
    }

    // Check for duplicate times
    const uniqueTimes = [...new Set(newSchedule.times)];
    if (uniqueTimes.length !== newSchedule.times.length) {
      alert("Có giờ đăng bị trùng lặp!");
      return;
    }

    onEdit(editingIndex, {
      ...schedules[editingIndex],
      date: newSchedule.date,
      times: newSchedule.times,
    });

    // Reset form
    setNewSchedule({ date: "", times: ["08:00"] });
    setEditingIndex(null);
  };

  const handleCancelEdit = () => {
    setNewSchedule({ date: "", times: ["08:00"] });
    setEditingIndex(null);
  };

  // Filter schedules
  const filteredSchedules = schedules.filter((schedule) => {
    const status = getScheduleStatus(schedule);
    if (filter === "pending") return status === "pending";
    if (filter === "completed") return status === "completed";
    return true;
  });

  // Sort schedules by date
  const sortedSchedules = [...filteredSchedules].sort((a, b) => {
    return new Date(a.date) - new Date(b.date);
  });

  // Count schedules
  const pendingCount = schedules.filter(
    (s) => getScheduleStatus(s) === "pending"
  ).length;
  const completedCount = schedules.filter((s) => s.completed).length;

  return (
    <div className="space-y-4">
      {/* Header with stats and filter */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div className="flex gap-4 text-sm">
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <span className="text-gray-600">
              Chờ đăng: <span className="font-medium">{pendingCount}</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
            <span className="text-gray-600">
              Đã đăng: <span className="font-medium">{completedCount}</span>
            </span>
          </div>
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => setFilter("all")}
            className={`px-3 py-1 text-sm rounded-md ${
              filter === "all"
                ? "bg-blue-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Tất cả
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`px-3 py-1 text-sm rounded-md ${
              filter === "pending"
                ? "bg-blue-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Chờ đăng
          </button>
          <button
            onClick={() => setFilter("completed")}
            className={`px-3 py-1 text-sm rounded-md ${
              filter === "completed"
                ? "bg-blue-500 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            Đã đăng
          </button>
        </div>
      </div>

      {/* Add new schedule button - only show if there are existing schedules */}
      {!isAddingNew && editingIndex === null && schedules.length > 0 && (
        <button
          onClick={() => setIsAddingNew(true)}
          className="w-full flex items-center justify-center gap-2 py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50 transition-colors"
        >
          <FiPlus size={20} />
          <span className="font-medium">Thêm lịch đăng mới</span>
        </button>
      )}

      {/* Add/Edit form */}
      {(isAddingNew || editingIndex !== null) && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-4">
          <h3 className="font-medium text-gray-800 flex items-center gap-2">
            <FiCalendar className="text-blue-600" />
            {editingIndex !== null
              ? "Chỉnh sửa lịch đăng"
              : "Thêm lịch đăng mới"}
          </h3>

          {/* Date and Time pickers in one row */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Date picker */}
            <div className="w-full md:w-1/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
              <label className="block text-gray-700 font-medium mb-2">
                Chọn ngày
              </label>
              <input
                type="date"
                value={newSchedule.date}
                onChange={(e) =>
                  setNewSchedule({ ...newSchedule, date: e.target.value })
                }
                min={new Date().toISOString().split("T")[0]}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Time pickers */}
            <div className="w-full md:w-2/3 p-3 border border-gray-200 rounded-md bg-white shadow-sm">
              <label className="block text-gray-700 font-medium mb-2">
                Giờ đăng bài
              </label>
              <div className="space-y-3">
                {newSchedule.times.map((time, index) => {
                  const [hours, minutes] = time.split(":");
                  return (
                    <div key={index} className="flex items-center gap-2">
                      <div className="relative flex-1 flex shadow-sm rounded-md overflow-hidden border border-gray-200">
                        {/* Dropdown giờ */}
                        <div className="relative w-1/2">
                          <select
                            value={hours}
                            onChange={(e) => {
                              const newHours = e.target.value;
                              handleTimeChange(index, `${newHours}:${minutes}`);
                            }}
                            className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                          >
                            {Array.from({ length: 24 }, (_, i) => {
                              const hour = i < 10 ? `0${i}` : `${i}`;
                              return (
                                <option key={hour} value={hour}>
                                  {hour}
                                </option>
                              );
                            })}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                            <svg
                              className="w-4 h-4 fill-current"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>

                        {/* Dấu hai chấm */}
                        <div className="flex items-center justify-center bg-gray-50 px-2">
                          <span className="text-gray-500 font-medium">:</span>
                        </div>

                        {/* Dropdown phút */}
                        <div className="relative w-1/2">
                          <select
                            value={minutes}
                            onChange={(e) => {
                              const newMinutes = e.target.value;
                              handleTimeChange(index, `${hours}:${newMinutes}`);
                            }}
                            className="w-full h-full px-4 py-2.5 text-gray-700 bg-white focus:ring-2 focus:ring-blue-500 border-none focus:outline-none appearance-none text-center font-medium"
                          >
                            {(() => {
                              const isDev = process.env.NODE_ENV === 'development';
                              const step = isDev ? 1 : 5;
                              const count = isDev ? 60 : 12;
                              return Array.from({ length: count }, (_, i) => {
                                const minute = i * step;
                                const formattedMinute =
                                  minute < 10 ? `0${minute}` : `${minute}`;
                                return (
                                  <option
                                    key={formattedMinute}
                                    value={formattedMinute}
                                  >
                                    {formattedMinute}
                                  </option>
                                );
                              });
                            })()}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
                            <svg
                              className="w-4 h-4 fill-current"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                clipRule="evenodd"
                              />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveTime(index)}
                        className={`p-2 text-white ${
                          newSchedule.times.length > 1
                            ? "bg-red-500 hover:bg-red-600"
                            : "bg-gray-300 cursor-not-allowed"
                        } rounded-full transition-colors focus:outline-none ${
                          newSchedule.times.length > 1
                            ? "focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                            : ""
                        }`}
                        aria-label="Xóa giờ đăng"
                        disabled={newSchedule.times.length <= 1}
                        title={
                          newSchedule.times.length <= 1
                            ? "Phải có ít nhất một giờ đăng bài"
                            : "Xóa giờ đăng này"
                        }
                      >
                        <FiTrash2 size={16} />
                      </button>
                    </div>
                  );
                })}
                <button
                  type="button"
                  onClick={handleAddTime}
                  className="w-full px-4 py-2.5 bg-white text-blue-600 border border-blue-500 rounded-md hover:bg-blue-50 transition-colors duration-200 flex items-center justify-center font-medium shadow-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z"
                      clipRule="evenodd"
                    />
                  </svg>
                  Thêm giờ đăng
                </button>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex gap-2">
            <button
              onClick={editingIndex !== null ? handleSaveEdit : handleSaveNew}
              className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 font-medium"
            >
              {editingIndex !== null ? "Lưu thay đổi" : "Thêm lịch"}
            </button>
            {/* Only show cancel button if there are existing schedules or editing */}
            {(schedules.length > 0 || editingIndex !== null) && (
              <button
                onClick={
                  editingIndex !== null ? handleCancelEdit : handleCancelNew
                }
                className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 font-medium"
              >
                Hủy
              </button>
            )}
          </div>
        </div>
      )}

      {/* Schedule list */}
      <div className="space-y-3">
        {sortedSchedules.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <FiCalendar size={48} className="mx-auto mb-3 opacity-30" />
            <p>
              {filter === "all"
                ? "Chưa có lịch đăng nào"
                : filter === "pending"
                ? "Không có lịch chờ đăng"
                : "Không có lịch đã đăng"}
            </p>
          </div>
        ) : (
          sortedSchedules.map((schedule, index) => {
            const originalIndex = schedules.indexOf(schedule);
            const status = getScheduleStatus(schedule);

            return (
              <div
                key={schedule.id || index}
                className={`border rounded-lg p-4 ${
                  status === "completed"
                    ? "bg-green-50 border-green-200"
                    : status === "overdue"
                    ? "bg-red-50 border-red-200"
                    : "bg-white border-gray-200"
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    {/* Date */}
                    <div className="flex items-center gap-2 mb-2">
                      <FiCalendar
                        className={
                          status === "completed"
                            ? "text-green-600"
                            : status === "overdue"
                            ? "text-red-600"
                            : "text-blue-600"
                        }
                      />
                      <span className="font-medium text-gray-800">
                        {formatDate(schedule.date)}
                      </span>
                      {status === "completed" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded-full">
                          <FiCheckCircle size={12} />
                          Đã đăng
                        </span>
                      )}
                      {status === "overdue" && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-100 text-red-700 text-xs rounded-full">
                          <FiAlertCircle size={12} />
                          Quá hạn
                        </span>
                      )}
                    </div>

                    {/* Times */}
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <FiClock size={14} />
                      <span>{schedule.times.join(", ")}</span>
                    </div>

                    {/* Completed timestamp */}
                    {schedule.completed && schedule.completedAt && (
                      <div className="mt-2 text-xs text-gray-500">
                        Đã đăng lúc:{" "}
                        {new Date(schedule.completedAt).toLocaleString("vi-VN")}
                      </div>
                    )}
                  </div>

                  {/* Action buttons */}
                  <div className="flex gap-2">
                    {!schedule.completed && (
                      <>
                        <button
                          onClick={() => handleEditSchedule(originalIndex)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-md"
                          title="Chỉnh sửa"
                        >
                          <FiEdit2 size={18} />
                        </button>
                        <button
                          onClick={() => {
                            if (
                              window.confirm(
                                "Bạn có chắc muốn xóa lịch đăng này?"
                              )
                            ) {
                              onDelete(originalIndex);
                            }
                          }}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md"
                          title="Xóa"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </>
                    )}
                    {schedule.completed && (
                      <button
                        onClick={() => onToggleCompleted(originalIndex)}
                        className="p-2 text-gray-600 hover:bg-gray-100 rounded-md text-sm"
                        title="Đặt lại về chờ đăng"
                      >
                        Reset
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default FixedDateScheduleUI;
